"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { isTrackingAllowed } from "@/lib/consent/privacy";

/**
 * Manually fires a PostHog `$pageview` event whenever the App Router pathname
 * changes via SPA navigation. Necessary because Next.js App Router uses
 * client-side `<Link>` navigation (history.pushState), which the inline
 * posthog-js snippet does not auto-detect — verified empirically on
 * 2026-05-05 that `$autocapture` and `$$heatmap` fire on SPA navs but
 * `$pageview` does not.
 *
 * Skips the first render. PostHogTracker sends one initial pageview from the
 * SDK loaded callback after applying shared consent. SPA views are sent only
 * after the SDK is ready, so no pre-consent views can queue for later replay.
 *
 * Reads `window.location.href` inside the effect rather than depending on
 * `useSearchParams` to avoid forcing the entire app into dynamic rendering
 * (which would break `output: "export"` static export).
 *
 * PostHog respects the global opt-out state set in PostHogTracker, so this
 * `capture()` call is a no-op when consent has not been granted.
 */
export default function PostHogPageView() {
  const pathname = usePathname();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!isTrackingAllowed() || !window.__posthog?.loaded || !window.posthog?.capture) return;
    window.posthog.capture("$pageview", {
      $current_url: window.location.href,
    });
  }, [pathname]);

  return null;
}
